import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { PlanDefinition, PlanLimits } from '@worki/shared';
import { api } from '../../lib/api.js';
import { Button, Input, Spinner } from '../../components/ui.js';
import { Modal } from '../../components/Modal.js';
import { AdminCard, useAdminAction } from './parts.js';

/**
 * ADM-007 — the price list.
 *
 * What ships in the code is the starting point; what this screen stores is an
 * override on top of it, so a plan added in a later release appears by itself
 * instead of staying invisible until somebody re-saves this page.
 *
 * `null` means unlimited and is entered as an empty field rather than as a very
 * large number: a cap of 999999 still prints a cap, and every comparison
 * against it has to remember the number is a lie.
 */
const CAPS: Array<keyof PlanLimits> = ['connectedMailAccounts', 'galleries', 'quotesPerMonth', 'voiceActionsPerMonth'];

export function PackagesSection() {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<PlanDefinition | null>(null);

  const list = useQuery({
    queryKey: ['platform', 'packages'],
    queryFn: () => api.get<{ items: PlanDefinition[] }>('/platform/packages'),
  });

  const action = useAdminAction([
    ['platform', 'packages'],
    ['billing'],
  ]);

  if (list.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  const shekels = (agorot: number) => (agorot / 100).toLocaleString('en-US', { maximumFractionDigits: 2 });
  const cap = (value: number | null) => (value === null ? t('admin.packages.unlimitedShort') : String(value));

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">{t('admin.packages.hint')}</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {(list.data?.items ?? []).map((plan) => (
          <AdminCard
            key={plan.id}
            title={t(`billing.plans.${plan.id}`)}
            action={
              <button
                type="button"
                onClick={() => setEditing(plan)}
                className="h-8 px-3 rounded-lg border border-border bg-card text-xs font-medium hover:bg-muted transition-colors"
              >
                {t('common.edit')}
              </button>
            }
          >
            <p className="text-2xl font-semibold ltr-nums">
              ₪{shekels(plan.monthlyPriceAgorot)}
              <span className="text-xs text-muted-foreground font-normal"> / {t('billing.perMonth')}</span>
            </p>
            <dl className="space-y-1.5 pt-1">
              {CAPS.map((key) => (
                <div key={key} className="flex items-center justify-between gap-2 text-xs">
                  <dt className="text-muted-foreground truncate">{t(`admin.packages.${key}`)}</dt>
                  <dd className="font-medium ltr-nums shrink-0">{cap(plan.limits[key])}</dd>
                </div>
              ))}
            </dl>
          </AdminCard>
        ))}
      </div>

      <EditPlanDialog
        plan={editing}
        busy={action.busy}
        onClose={() => setEditing(null)}
        onSave={(input) => {
          const target = editing;
          if (!target) return;
          void action.run(() => api.put(`/platform/packages/${target.id}`, input), 'admin.packages.saved').then((ok) => {
            if (ok) setEditing(null);
          });
        }}
      />
    </div>
  );
}

function EditPlanDialog({
  plan,
  busy,
  onClose,
  onSave,
}: {
  plan: PlanDefinition | null;
  busy: boolean;
  onClose: () => void;
  onSave: (input: { monthlyPriceAgorot: number; limits: PlanLimits }) => void;
}) {
  const { t } = useTranslation();
  const [price, setPrice] = useState('');
  const [caps, setCaps] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!plan) return;
    setPrice(String(plan.monthlyPriceAgorot / 100));
    setCaps(
      Object.fromEntries(CAPS.map((key) => [key, plan.limits[key] === null ? '' : String(plan.limits[key])])),
    );
  }, [plan]);

  const submit = () => {
    const limits = Object.fromEntries(
      CAPS.map((key) => [key, caps[key]?.trim() === '' || caps[key] === undefined ? null : Number(caps[key])]),
    ) as unknown as PlanLimits;
    onSave({ monthlyPriceAgorot: Math.round((Number(price) || 0) * 100), limits });
  };

  return (
    <Modal
      open={plan !== null}
      title={plan ? t(`billing.plans.${plan.id}`) : ''}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} loading={busy}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block space-y-1">
          <span className="text-xs font-medium text-muted-foreground">{t('admin.packages.price')}</span>
          <Input
            value={price}
            onChange={(event) => setPrice(event.target.value.replace(/[^\d.]/g, ''))}
            inputMode="decimal"
            className="ltr-nums"
          />
          <span className="text-[11px] text-muted-foreground">{t('admin.packages.priceHint')}</span>
        </label>

        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">{t('admin.packages.limits')}</p>
          <div className="grid grid-cols-2 gap-2">
            {CAPS.map((key) => (
              <label key={key} className="block space-y-1">
                <span className="text-[11px] text-muted-foreground">{t(`admin.packages.${key}`)}</span>
                <Input
                  value={caps[key] ?? ''}
                  onChange={(event) =>
                    setCaps((current) => ({ ...current, [key]: event.target.value.replace(/\D/g, '').slice(0, 6) }))
                  }
                  inputMode="numeric"
                  placeholder={t('admin.packages.unlimited')}
                  className="ltr-nums"
                />
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
